class Carousel {
    constructor(element, options = {}) {
        this.root = element;
        const elementOptions = Carousel.getElementOptions(element);
        this.options = {
            autoStep: false,
            interval: 2500,
            align: 'start',
            startIndex: 0,
            loop: false,
            readOnly: false,
            trackActive: false,
            snap: false,
            ...elementOptions,
            ...options,
        };

        this.track = this.root.querySelector('.carousel__track');
        this.list = this.root.querySelector('.carousel__list');
        this.originalItems = [];
        this.items = [];
        this.itemOffsets = new Map();
        this.paginationButtons = [];
        this.activeItem = null;
        this.activePaginationButton = null;
        this.activeIndex = this.options.startIndex;
        this.loopStartIndex = 0;
        this.loopEndIndex = 0;
        this.loopWidth = 0;
        this.maxScrollLeft = 0;
        this.stepTimer = null;
        this.loopPreparationFrame = null;
        this.snapTimeout = null;
        this.snapAnimationFrame = null;
        this.wheelSnapTimeout = null;
        this.snapStartScrollLeft = 0;
        this.hasUserInteracted = false;
        this.isDragging = false;
        this.isProgrammaticScroll = false;
        this.isResettingLoopScroll = false;
        this.dragStartX = 0;
        this.dragStartScrollLeft = 0;
        this.isVisible = true;
        this.isInitialized = false;
        this.initScheduled = false;
        this.initObserver = null;
        this.cancelInitTimer = null;
        this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.intersectionObserver = null;
        this.eventCleanups = [];
        this.paginationEventCleanups = [];
        this.endDragOnWindow = () => this.endDrag();
    }

    init() {
        if (this.isInitialized) {
            return true;
        }

        this.clearScheduledInit();

        const existingInstance = Carousel.getInstance(this.root);
        if (existingInstance && existingInstance !== this) {
            return false;
        }

        if (!this.track || !this.list) {
            return false;
        }

        this.originalItems = Array.from(this.list.querySelectorAll('.carousel__item'));
        this.items = [...this.originalItems];
        this.paginationButtons = Array.from(this.root.querySelectorAll('.carousel__pagination-button'));

        if (!this.originalItems.length) {
            return false;
        }

        this.bindEvents();
        this.bindPaginationEvents();
        Carousel.register(this);
        this.isInitialized = true;
        this.root.classList.toggle('is-read-only', this.options.readOnly);

        if (this.options.loop && !this.isReducedMotion) {
            this.prepareStepLoop();
        } else {
            this.measureLoop();
            this.completeSetup();
        }

        return true;
    }

    scheduleInit() {
        if (this.isInitialized || this.initScheduled) {
            return this.isInitialized;
        }

        this.initScheduled = true;

        const runInit = () => {
            if (!this.initScheduled) {
                return;
            }

            this.clearScheduledInit();
            this.init();
        };

        if ('IntersectionObserver' in window) {
            this.initObserver = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    runInit();
                }
            }, { rootMargin: '200px 0px' });
            this.initObserver.observe(this.root);
        }

        if ('requestIdleCallback' in window) {
            const handle = window.requestIdleCallback(runInit, { timeout: 2000 });
            this.cancelInitTimer = () => window.cancelIdleCallback(handle);
        } else {
            const handle = window.setTimeout(runInit, 200);
            this.cancelInitTimer = () => window.clearTimeout(handle);
        }

        return false;
    }

    clearScheduledInit() {
        this.initScheduled = false;

        if (this.initObserver) {
            this.initObserver.disconnect();
            this.initObserver = null;
        }

        if (this.cancelInitTimer) {
            this.cancelInitTimer();
            this.cancelInitTimer = null;
        }
    }

    bindEvents() {
        if (!this.options.readOnly) {
            ['touchstart', 'wheel', 'focusin'].forEach((eventName) => {
                this.addEvent(this.track, eventName, () => this.handleUserInteraction(), { passive: true });
            });

            this.addEvent(this.track, 'pointerdown', (event) => this.startDrag(event));
            this.addEvent(this.track, 'pointermove', (event) => this.drag(event));
        }

        if (this.options.loop && !this.options.readOnly) {
            this.addEvent(this.track, 'scroll', () => this.resetManualLoopScroll(), { passive: true });
        }

        if (this.options.snap && !this.options.readOnly) {
            this.addEvent(this.track, 'touchstart', () => this.startSnapInteraction(), { passive: true });
            this.addEvent(this.track, 'touchend', () => this.endSnapInteraction(), { passive: true });
            this.addEvent(this.track, 'wheel', () => this.scheduleWheelSnap(), { passive: true });
        }

        this.root.querySelectorAll('.carousel__arrow--prev').forEach((button) => {
            this.addEvent(button, 'click', () => {
                this.handleUserInteraction();
                this.prev();
            });
        });

        this.root.querySelectorAll('.carousel__arrow--next').forEach((button) => {
            this.addEvent(button, 'click', () => {
                this.handleUserInteraction();
                this.next();
            });
        });

        if (this.options.autoStep && 'IntersectionObserver' in window) {
            this.intersectionObserver = new IntersectionObserver((entries) => {
                this.isVisible = entries[0].isIntersecting;

                if (this.isVisible) {
                    if (!this.isReducedMotion && !this.hasUserInteracted && !this.stepTimer) {
                        this.startAutoStep();
                    }

                    return;
                }

                this.stopAutoStep();
            });
            this.intersectionObserver.observe(this.root);
        }
    }

    addEvent(target, eventName, handler, options = false, cleanups = this.eventCleanups) {
        target.addEventListener(eventName, handler, options);
        cleanups.push(() => target.removeEventListener(eventName, handler, options));
    }

    bindPaginationEvents() {
        this.clearPaginationEvents();

        this.paginationButtons.forEach((button) => {
            this.addEvent(
                button,
                'click',
                () => {
                    this.handleUserInteraction();
                    this.goToRealIndex(Number(button.dataset.carouselIndex) || 0, 'animated');
                },
                false,
                this.paginationEventCleanups
            );
        });
    }

    clearPaginationEvents() {
        this.paginationEventCleanups.forEach((cleanup) => cleanup());
        this.paginationEventCleanups = [];
    }

    clearEvents() {
        this.eventCleanups.forEach((cleanup) => cleanup());
        this.eventCleanups = [];
    }

    refresh(startIndex = 0) {
        if (!this.isInitialized) {
            this.activeIndex = startIndex;
            this.init();
            return;
        }

        if (!this.track || !this.list) {
            return;
        }

        this.stopAutoStep();
        this.cancelPendingSnap();
        this.clearLoopPreparation();
        this.clearActiveItem();
        this.clearActivePagination();

        this.list.querySelectorAll('[data-carousel-clone="true"]').forEach((clone) => clone.remove());
        this.originalItems = Array.from(this.list.querySelectorAll('.carousel__item'));
        this.items = [...this.originalItems];
        this.paginationButtons = Array.from(this.root.querySelectorAll('.carousel__pagination-button'));
        this.activeIndex = startIndex;
        this.loopStartIndex = 0;
        this.loopEndIndex = 0;
        this.loopWidth = 0;
        this.maxScrollLeft = 0;
        this.itemOffsets = new Map();
        this.track.scrollLeft = 0;
        this.bindPaginationEvents();

        if (!this.originalItems.length) {
            return;
        }

        if (this.options.loop && !this.isReducedMotion) {
            this.prepareStepLoop();
        } else {
            this.measureLoop();
            this.completeSetup();
        }
    }

    resetManualLoopScroll() {
        if (
            this.isProgrammaticScroll
            || this.isResettingLoopScroll
            || !this.hasLoopClones()
            || !this.loopWidth
        ) {
            return;
        }

        const firstRealItem = this.items[this.loopStartIndex];
        const firstRightCloneItem = this.items[this.loopEndIndex];

        if (!firstRealItem || !firstRightCloneItem) {
            return;
        }

        const leftBoundary = this.getItemScrollLeft(firstRealItem);
        const rightBoundary = this.getItemScrollLeft(firstRightCloneItem);
        const currentScrollLeft = this.track.scrollLeft;
        let nextScrollLeft = currentScrollLeft;

        while (nextScrollLeft < leftBoundary) {
            nextScrollLeft += this.loopWidth;
        }

        while (nextScrollLeft >= rightBoundary) {
            nextScrollLeft -= this.loopWidth;
        }

        if (Math.abs(nextScrollLeft - currentScrollLeft) < 1) {
            return;
        }

        const scrollDelta = nextScrollLeft - currentScrollLeft;
        this.isResettingLoopScroll = true;
        this.track.scrollLeft = nextScrollLeft;
        this.snapStartScrollLeft += scrollDelta;
        this.dragStartScrollLeft += scrollDelta;

        window.requestAnimationFrame(() => {
            this.isResettingLoopScroll = false;
        });
    }

    startDrag(event) {
        if (this.isDragging || event.pointerType !== 'mouse' || event.button !== 0) {
            return;
        }

        this.handleUserInteraction();
        this.isDragging = true;
        this.dragStartX = event.clientX;
        this.dragStartScrollLeft = this.track.scrollLeft;
        this.root.classList.add('is-dragging');
        window.addEventListener('pointerup', this.endDragOnWindow);
        window.addEventListener('pointercancel', this.endDragOnWindow);
    }

    drag(event) {
        if (!this.isDragging) {
            return;
        }

        event.preventDefault();
        this.track.scrollLeft = this.dragStartScrollLeft - (event.clientX - this.dragStartX);
    }

    endDrag() {
        if (!this.isDragging) {
            return;
        }

        this.isDragging = false;
        this.root.classList.remove('is-dragging');
        window.removeEventListener('pointerup', this.endDragOnWindow);
        window.removeEventListener('pointercancel', this.endDragOnWindow);

        if (this.options.snap) {
            this.endSnapInteraction();
        }
    }

    goToIndex(index, behavior = 'auto') {
        const normalizedIndex = this.normalizeIndex(index);
        const activeItem = this.items[normalizedIndex];

        if (!activeItem) {
            return;
        }

        this.activeIndex = normalizedIndex;
        if (this.options.trackActive && behavior !== 'animated' && behavior !== 'smooth') {
            this.setActiveItem(normalizedIndex);
        }
        this.setActivePagination(normalizedIndex);
        this.dispatchChangeEvent(normalizedIndex);

        const targetScrollLeft = this.clampScrollLeft(this.getItemScrollLeft(activeItem));

        this.scrollToPosition(targetScrollLeft, behavior, normalizedIndex);
    }

    dispatchChangeEvent(index) {
        this.root.dispatchEvent(new CustomEvent('carousel:change', {
            detail: {
                index: this.getRealIndex(index),
                activeIndex: index,
                item: this.items[index] || null,
            },
        }));
    }

    normalizeIndex(index) {
        if (this.hasLoopClones()) {
            return (index + this.items.length) % this.items.length;
        }

        return Math.min(Math.max(index, 0), this.getMaxReachableIndex());
    }

    next(behavior = 'animated') {
        if (this.isProgrammaticScroll) {
            return;
        }

        this.goToIndex(this.activeIndex + 1, this.isReducedMotion ? 'auto' : behavior);
    }

    prev(behavior = 'animated') {
        if (this.isProgrammaticScroll) {
            return;
        }

        this.goToIndex(this.activeIndex - 1, this.isReducedMotion ? 'auto' : behavior);
    }

    goToRealIndex(index, behavior = 'animated') {
        if (this.isProgrammaticScroll || !this.originalItems.length) {
            return;
        }

        const realIndex = (index + this.originalItems.length) % this.originalItems.length;
        const targetIndex = this.hasLoopClones()
            ? this.loopStartIndex + realIndex
            : realIndex;

        this.goToIndex(targetIndex, this.isReducedMotion ? 'auto' : behavior);
    }

    hasLoopClones() {
        return this.options.loop
            && this.items.length > this.originalItems.length
            && this.loopEndIndex > this.loopStartIndex;
    }

    isLoopCloneIndex(index = this.activeIndex) {
        return this.hasLoopClones() && (index < this.loopStartIndex || index >= this.loopEndIndex);
    }

    completeTransition(index) {
        let activeIndex = index;

        if (this.isLoopCloneIndex(index)) {
            const realIndex = this.getRealIndex(index);
            const rebasedIndex = this.loopStartIndex + realIndex;

            activeIndex = rebasedIndex;
            this.activeIndex = rebasedIndex;
            this.root.classList.add('is-loop-resetting');
            this.isResettingLoopScroll = true;
            this.track.scrollLeft = this.getItemScrollLeft(this.items[rebasedIndex]);

            this.setActivePagination(rebasedIndex);

            window.requestAnimationFrame(() => {
                this.isResettingLoopScroll = false;
                this.root.classList.remove('is-loop-resetting');
            });
        }

        if (this.options.trackActive) {
            this.setActiveItem(activeIndex);
        }

        this.snapStartScrollLeft = this.track.scrollLeft;
        this.isProgrammaticScroll = false;
    }

    setActiveItem(index) {
        const activeItem = this.items[index] || null;

        if (this.activeItem === activeItem) {
            return;
        }

        this.clearActiveItem();

        if (activeItem) {
            activeItem.classList.add('is-active');
        }

        this.activeItem = activeItem;
    }

    clearActiveItem() {
        if (!this.activeItem) {
            return;
        }

        this.activeItem.classList.remove('is-active');
        this.activeItem = null;
    }

    setActivePagination(index) {
        if (!this.paginationButtons.length) {
            this.clearActivePagination();

            return;
        }

        const realIndex = this.getRealIndex(index);
        const activePaginationButton = this.paginationButtons[realIndex] || null;

        if (this.activePaginationButton === activePaginationButton) {
            return;
        }

        if (this.activePaginationButton) {
            this.activePaginationButton.classList.remove('is-active');
        }

        if (activePaginationButton) {
            activePaginationButton.classList.add('is-active');
        }

        this.activePaginationButton = activePaginationButton;
    }

    clearActivePagination() {
        if (!this.activePaginationButton) {
            return;
        }

        this.activePaginationButton.classList.remove('is-active');
        this.activePaginationButton = null;
    }

    getRealIndex(index) {
        if (!this.hasLoopClones()) {
            return Math.min(Math.max(index, 0), this.originalItems.length - 1);
        }

        return (index - this.loopStartIndex + this.originalItems.length) % this.originalItems.length;
    }

    getItemScrollLeft(item) {
        const itemOffset = this.getItemOffset(item);

        if (this.options.align === 'center') {
            return itemOffset - (this.track.clientWidth - item.clientWidth) / 2;
        }

        return itemOffset;
    }

    getItemOffset(item) {
        if (this.itemOffsets.has(item)) {
            return this.itemOffsets.get(item);
        }

        return this.getItemOffsetFromDom(item);
    }

    getItemOffsetFromDom(item) {
        const itemRect = item.getBoundingClientRect();
        const trackRect = this.track.getBoundingClientRect();

        return itemRect.left - trackRect.left + this.track.scrollLeft;
    }

    getMaxReachableIndex() {
        const maxScrollLeft = this.getMaxScrollLeft();

        if (!maxScrollLeft) {
            return 0;
        }

        for (let index = 0; index < this.items.length; index++) {
            if (this.getItemScrollLeft(this.items[index]) >= maxScrollLeft) {
                return index;
            }
        }

        return this.items.length - 1;
    }

    getMaxScrollLeft() {
        return this.maxScrollLeft || Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    clampScrollLeft(scrollLeft) {
        return Math.min(Math.max(scrollLeft, 0), this.getMaxScrollLeft());
    }

    scrollToPosition(left, behavior, targetIndex) {
        if (behavior === 'animated' || behavior === 'smooth') {
            this.animateScrollTo(left, 460, targetIndex);
            return;
        }

        this.isProgrammaticScroll = true;
        this.track.scrollLeft = left;
        this.completeTransition(targetIndex);
    }

    animateScrollTo(targetScrollLeft, duration, targetIndex) {
        if (this.snapAnimationFrame) {
            window.cancelAnimationFrame(this.snapAnimationFrame);
        }

        this.isProgrammaticScroll = true;
        const startScrollLeft = this.track.scrollLeft;
        const distance = targetScrollLeft - startScrollLeft;

        if (Math.abs(distance) < 1) {
            this.track.scrollLeft = targetScrollLeft;
            this.completeTransition(targetIndex);
            return;
        }

        const startedAt = performance.now();

        const animate = (time) => {
            const progress = Math.min((time - startedAt) / duration, 1);
            const easedProgress = progress < 0.5
                ? 4 * Math.pow(progress, 3)
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            this.track.scrollLeft = startScrollLeft + distance * easedProgress;

            if (progress < 1) {
                this.snapAnimationFrame = window.requestAnimationFrame(animate);
                return;
            }

            this.snapAnimationFrame = null;
            this.completeTransition(targetIndex);
        };

        this.snapAnimationFrame = window.requestAnimationFrame(animate);
    }

    startSnapInteraction() {
        if (this.isProgrammaticScroll) {
            return;
        }

        this.cancelPendingSnap();

        this.snapStartScrollLeft = this.track.scrollLeft;
    }

    endSnapInteraction() {
        if (this.isProgrammaticScroll) {
            return;
        }

        if (Math.abs(this.track.scrollLeft - this.snapStartScrollLeft) < 2) {
            return;
        }

        this.scheduleSnap();
    }

    scheduleWheelSnap() {
        if (this.isProgrammaticScroll) {
            return;
        }

        if (!this.wheelSnapTimeout) {
            this.startSnapInteraction();
        }

        window.clearTimeout(this.wheelSnapTimeout);
        this.wheelSnapTimeout = window.setTimeout(() => {
            this.wheelSnapTimeout = null;
            this.endSnapInteraction();
        }, 120);
    }

    scheduleSnap() {
        window.clearTimeout(this.snapTimeout);
        this.snapTimeout = window.setTimeout(() => this.snapToNearestByDirection());
    }

    snapToNearestByDirection() {
        this.snapTimeout = null;

        const delta = this.track.scrollLeft - this.snapStartScrollLeft;

        if (Math.abs(delta) < 2) {
            return;
        }

        const direction = delta > 0 ? 1 : -1;
        const nearestIndex = this.getNearestItemIndex();
        const targetIndex = nearestIndex === this.activeIndex
            ? this.activeIndex + direction
            : nearestIndex;
        const normalizedIndex = this.normalizeIndex(targetIndex);
        const targetScrollLeft = this.clampScrollLeft(this.getItemScrollLeft(this.items[normalizedIndex]));

        if (Math.abs(this.track.scrollLeft - targetScrollLeft) <= 6) {
            this.goToIndex(normalizedIndex, 'auto');
            return;
        }

        this.goToIndex(normalizedIndex, 'animated');
    }

    cancelPendingSnap() {
        window.clearTimeout(this.snapTimeout);
        window.clearTimeout(this.wheelSnapTimeout);
        this.snapTimeout = null;
        this.wheelSnapTimeout = null;

        if (this.snapAnimationFrame) {
            window.cancelAnimationFrame(this.snapAnimationFrame);
            this.snapAnimationFrame = null;
        }

        this.isProgrammaticScroll = false;
    }

    getNearestItemIndex() {
        if (!this.items.length) {
            return -1;
        }

        const currentScrollLeft = this.track.scrollLeft;
        let nearestIndex = 0;
        let nearestDistance = Math.abs(currentScrollLeft - this.getItemScrollLeft(this.items[0]));

        this.items.forEach((item, index) => {
            const distance = Math.abs(currentScrollLeft - this.getItemScrollLeft(item));

            if (distance < nearestDistance) {
                nearestIndex = index;
                nearestDistance = distance;
            }
        });

        return nearestIndex;
    }

    getCloneSetsCount(originalWidth) {
        const requiredSets = Math.ceil((originalWidth + this.track.clientWidth) / originalWidth) - 1;

        return Math.min(Math.max(1, requiredSets), Carousel.maxCloneSets);
    }

    prepareStepLoop(attempt = 0) {
        // Centered step loop needs clones on both sides so edge items keep neighbors.
        this.updateTrackWidth();

        const originalWidth = this.getItemsWidth(this.originalItems);

        if (!originalWidth) {
            if (attempt < 10) {
                this.loopPreparationFrame = window.requestAnimationFrame(() => {
                    this.loopPreparationFrame = null;
                    this.prepareStepLoop(attempt + 1);
                });
            } else {
                this.measureLoop();
                this.completeSetup();
            }

            return;
        }

        const cloneSetsCount = this.getCloneSetsCount(originalWidth);
        const startIndex = (this.activeIndex + this.originalItems.length) % this.originalItems.length;
        const leftFragment = document.createDocumentFragment();
        const rightFragment = document.createDocumentFragment();
        const leftClones = [];
        const rightClones = [];

        for (let i = 0; i < cloneSetsCount; i++) {
            this.originalItems.forEach((item) => {
                const leftClone = this.createClone(item);
                const rightClone = this.createClone(item);

                leftClones.push(leftClone);
                rightClones.push(rightClone);
                leftFragment.appendChild(leftClone);
                rightFragment.appendChild(rightClone);
            });
        }

        this.list.insertBefore(leftFragment, this.list.firstChild);
        this.list.appendChild(rightFragment);
        this.items = leftClones.concat(this.originalItems, rightClones);
        this.loopStartIndex = cloneSetsCount * this.originalItems.length;
        this.loopEndIndex = this.loopStartIndex + this.originalItems.length;
        this.activeIndex = this.loopStartIndex + startIndex;
        this.measureLoop();
        this.completeSetup();
    }

    clearLoopPreparation() {
        if (!this.loopPreparationFrame) {
            return;
        }

        window.cancelAnimationFrame(this.loopPreparationFrame);
        this.loopPreparationFrame = null;
    }

    completeSetup() {
        if (!this.isInitialized || !this.originalItems.length) {
            return;
        }

        this.goToIndex(this.activeIndex);

        if (this.options.autoStep && !this.isReducedMotion && !this.hasUserInteracted) {
            this.startAutoStep();
        }
    }

    createClone(item) {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.carouselClone = 'true';

        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((focusableElement) => {
            focusableElement.setAttribute('tabindex', '-1');
        });

        clone.querySelectorAll('img').forEach((image) => {
            image.loading = 'lazy';
            image.decoding = 'async';
            image.setAttribute('fetchpriority', 'low');
        });

        return clone;
    }

    measureLoop() {
        this.updateTrackWidth();
        this.measureMetrics();
    }

    measureMetrics() {
        if (!this.originalItems.length) {
            this.loopWidth = 0;
            this.maxScrollLeft = 0;
            this.itemOffsets = new Map();
            return;
        }

        this.measureItemMetrics();
        this.loopWidth = this.getLoopWidth();
    }

    measureItemMetrics() {
        const trackRect = this.track.getBoundingClientRect();
        const scrollLeft = this.track.scrollLeft;

        this.itemOffsets = new Map();
        this.items.forEach((item) => {
            const itemRect = item.getBoundingClientRect();
            this.itemOffsets.set(item, itemRect.left - trackRect.left + scrollLeft);
        });
        this.maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    getItemsWidth(items) {
        return items.reduce((width, item) => width + item.getBoundingClientRect().width, 0);
    }

    getLoopWidth() {
        const firstLoopItem = this.items[this.loopStartIndex];
        const nextLoopItem = this.items[this.loopStartIndex + this.originalItems.length];

        if (this.options.loop && firstLoopItem && nextLoopItem) {
            return this.getItemOffset(nextLoopItem) - this.getItemOffset(firstLoopItem);
        }

        return this.getItemsWidth(this.originalItems);
    }

    updateTrackWidth() {
        this.applyTrackWidth(this.track.clientWidth);
    }

    applyTrackWidth(width) {
        this.root.style.setProperty('--carousel-track-width', `${width}px`);
    }

    startAutoStep() {
        if (
            !this.originalItems.length
            || this.loopPreparationFrame
            || !this.isVisible
            || this.hasUserInteracted
        ) {
            return;
        }

        this.stopAutoStep();

        this.stepTimer = window.setInterval(() => {
            if (!this.isVisible) {
                return;
            }

            this.next();
        }, this.options.interval);
    }

    stopAutoStep() {
        if (this.stepTimer) {
            window.clearInterval(this.stepTimer);
            this.stepTimer = null;
        }
    }

    handleUserInteraction() {
        this.hasUserInteracted = true;
        this.stopAutoStep();
    }

    destroy() {
        this.clearScheduledInit();
        this.stopAutoStep();
        this.cancelPendingSnap();
        this.clearLoopPreparation();
        this.clearActiveItem();
        this.clearActivePagination();
        this.clearEvents();
        this.clearPaginationEvents();

        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }

        window.removeEventListener('pointerup', this.endDragOnWindow);
        window.removeEventListener('pointercancel', this.endDragOnWindow);

        if (this.list) {
            this.list.querySelectorAll('[data-carousel-clone="true"]').forEach((clone) => clone.remove());
        }

        this.root.classList.remove('is-dragging', 'is-loop-resetting', 'is-read-only');
        this.originalItems = [];
        this.items = [];
        this.paginationButtons = [];
        this.itemOffsets = new Map();
        this.activeIndex = this.options.startIndex;
        this.loopStartIndex = 0;
        this.loopEndIndex = 0;
        this.loopWidth = 0;
        this.maxScrollLeft = 0;
        this.hasUserInteracted = false;
        this.isDragging = false;
        this.isProgrammaticScroll = false;
        this.isResettingLoopScroll = false;
        this.isInitialized = false;

        Carousel.unregister(this);
    }

    static initAll(selector, options = {}) {
        const instances = [];

        document.querySelectorAll(selector).forEach((element) => {
            const existingInstance = Carousel.getInstance(element);

            if (existingInstance) {
                instances.push(existingInstance);
                return;
            }

            instances.push(new Carousel(element, options));
        });

        instances.forEach((instance) => instance.scheduleInit());

        return instances;
    }

    static getElementOptions(element) {
        if (!element.dataset.carouselOptions) {
            return {};
        }

        try {
            const options = JSON.parse(element.dataset.carouselOptions);

            if (!options || typeof options !== 'object' || Array.isArray(options)) {
                return {};
            }

            return options;
        } catch (_error) {
            return {};
        }
    }

    static register(instance) {
        Carousel.instances.add(instance);
        Carousel.elementInstances.set(instance.root, instance);

        if (Carousel.isResizeListenerBound) {
            return;
        }

        Carousel.isResizeListenerBound = true;
        window.addEventListener('resize', Carousel.sharedResizeHandler, { passive: true });
    }

    static unregister(instance) {
        Carousel.instances.delete(instance);

        if (Carousel.getInstance(instance.root) === instance) {
            Carousel.elementInstances.delete(instance.root);
        }

        if (Carousel.instances.size || !Carousel.isResizeListenerBound) {
            return;
        }

        Carousel.isResizeListenerBound = false;
        window.removeEventListener('resize', Carousel.sharedResizeHandler);
    }

    static getInstance(element) {
        return Carousel.elementInstances.get(element) || null;
    }

    static scheduleSharedMeasure() {
        if (Carousel.sharedResizeFrame) {
            window.cancelAnimationFrame(Carousel.sharedResizeFrame);
        }

        Carousel.sharedResizeFrame = window.requestAnimationFrame(() => {
            Carousel.sharedResizeFrame = null;

            const liveInstances = [];

            Carousel.instances.forEach((instance) => {
                if (!instance.root.isConnected) {
                    instance.destroy();
                    return;
                }

                liveInstances.push(instance);
            });

            const trackWidths = liveInstances.map((instance) => instance.track.clientWidth);
            liveInstances.forEach((instance, index) => instance.applyTrackWidth(trackWidths[index]));
            liveInstances.forEach((instance) => instance.measureMetrics());
        });
    }
}

Carousel.instances = new Set();
Carousel.elementInstances = new WeakMap();
Carousel.maxCloneSets = 10;
Carousel.isResizeListenerBound = false;
Carousel.sharedResizeFrame = null;
Carousel.sharedResizeHandler = () => Carousel.scheduleSharedMeasure();

export default Carousel;
